import { useState } from "react";
import Header from "../components/Header";
import PopUp from "../components/PopUp";
import SectionSecuritysectionTemp from "../components/SectionSecuritysectionTemp";

const LAST_UPDATED = "4 August 2026";

const Section = ({ title, children }) => (
  <div className="self-stretch flex flex-col items-start justify-start gap-[20px]">
    <h2 className="m-0 relative text-13xl tracking-[-1px] leading-[110%] font-normal font-libre-baskerville text-black mq450:text-5xl">
      {title}
    </h2>
    {children}
  </div>
);

const SubHeading = ({ children }) => (
  <h3 className="m-0 relative text-xl leading-[28px] font-medium font-inter text-black mq450:text-lg">
    {children}
  </h3>
);

const Para = ({ children }) => (
  <p className="m-0 relative leading-[28px] font-light">{children}</p>
);

const List = ({ items }) => (
  <ul className="m-0 pl-6 flex flex-col items-start justify-start gap-[10px] list-disc">
    {items.map((item) => (
      <li key={typeof item === "string" ? item : item.key} className="leading-[28px] font-light">
        {typeof item === "string" ? item : item.node}
      </li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="w-full relative rounded bg-whitesmoke-100 overflow-hidden flex flex-col items-start justify-start leading-[normal] tracking-[normal]">
      <section className="self-stretch bg-lightgoldenrodyellow overflow-hidden flex flex-col items-start justify-start pt-2.5 px-0 pb-0 box-border max-w-full">
        <Header onJoinWaitlist={() => setShowModal(true)} />
        <div className="self-stretch flex flex-col items-start justify-start pt-[60px] pb-[75px] pr-5 pl-[100px] box-border gap-[16px] max-w-full text-left text-33xl text-black font-libre-baskerville lg:pl-[50px] lg:box-border mq750:pl-[25px] mq750:pt-[40px] mq750:pb-[45px] mq750:box-border">
          <h1 className="m-0 relative text-inherit tracking-[-1px] leading-[110%] font-inherit mq1050:text-23xl mq450:text-12xl">
            <span>{`Privacy `}</span>
            <i>Policy</i>
          </h1>
          <div className="relative text-sm uppercase font-inter font-medium leading-[20px] text-darkslategray-100">
            Last updated: {LAST_UPDATED}
          </div>
        </div>
      </section>

      <section className="self-stretch flex flex-row items-start justify-center py-[75px] px-14 box-border max-w-full text-left text-lg text-darkslategray-100 font-inter lg:px-[50px] lg:box-border mq750:py-[45px] mq750:px-[25px] mq750:box-border">
        <div className="w-[900px] flex flex-col items-start justify-start gap-[50px] max-w-full mq750:gap-[35px]">
          {/* ---------- Intro ---------- */}
          <div className="self-stretch flex flex-col items-start justify-start gap-[20px]">
            <Para>
              Closetrove.com / Closetrove app ("Closetrove", "Us", "we" or
              "ours") is engaged in the business of providing wardrobe
              organising and digitalising service to the customer.
            </Para>
            <Para>
              When the term "customers" is used in this Privacy Policy and
              related documents, it shall mean: (i) visitors and users of our
              website and our apps (Android and IOS) as defined below; and (ii)
              customers who use our Pro/ Star / VIP / extra service who are also
              subject to the additional terms pursuant to our Pro/ Star / VIP /
              extra service Agreement ("Users", "you" or "your").
            </Para>
            <Para>
              For purposes of this policy, "Platform" refers to our iOS and
              Android apps and our website (
              <a
                href="https://www.closetrove.com"
                className="text-darkslategray-100 underline"
              >
                www.closetrove.com
              </a>
              ). Through the Platform, we provide tools to digitize your
              wardrobe, style own outfits, hire expert for organising your
              wardrobe and stylists, and access related services (the
              "Service"). In this policy the service offered to our customers by
              the Closetrove professional are referred to as "Partners".
            </Para>
            <Para>
              This policy explains the information we collect, how we use it,
              and how we share it.
            </Para>
            <Para>
              We are committed to respecting the privacy of our customers. We
              understand and acknowledge that our customers have a legitimate
              expectation of privacy, including an expectation that we will
              protect their identities and personal information from disclosure
              to third parties, and we are committed to upholding that
              expectation of privacy with very limited exceptions, as described
              below.
            </Para>
            <Para>
              This Privacy Policy also describes other important topics relating
              to your personal information and privacy. By using the Service or
              accessing the Platform, you confirm that you have read and
              understood this Privacy Policy and agree to be bound by it along
              with our Terms of Service. If you do not agree, please discontinue
              use of the Platform and Service.
            </Para>
          </div>

          {/* ---------- Collection of Information ---------- */}
          <Section title="Collection of Information">
            <SubHeading>Account Identifiers</SubHeading>
            <Para>When you register, we collect:</Para>
            <List
              items={["your name", "phone number", "email address", "User ID"]}
            />

            <SubHeading>Device information</SubHeading>
            <Para>
              When you visit our platforms, we automatically collect certain
              information about your device, including:
            </Para>
            <List
              items={[
                "browser/device type",
                "your Internet Service Provider (ISP)",
                "Referring/exit pages",
                "date/time stamp",
              ]}
            />

            <SubHeading>Commercial information</SubHeading>
            <Para>
              In connection with your use of the Service, we may collect:
            </Para>
            <List
              items={[
                "Payment information and purchase history",
                "customer services information",
                "customer relationship management and marketing information",
                "postal address with city, state and pin code (optional fields - to be collected only for booking at-home services)",
              ]}
            />

            <SubHeading>Information Received Directly from You</SubHeading>
            <Para>
              We may collect additional personal information about you in
              connection with your request for customer service.
            </Para>
            <Para>
              Additionally, you may choose to make other personal information
              available to us in connection with using our Pro Services.
            </Para>

            <SubHeading>
              Email Account Data (with your explicit permission)
            </SubHeading>
            <Para>
              If you choose to connect your email account, we access it solely
              to identify your identity and create your account in our platform.
              We do not read any information from our emails.
            </Para>
            <Para>
              Our use of data received from Google APIs complies with the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-darkslategray-100 underline"
              >
                Google API Services User Data Policy
              </a>
              , including its Limited Use requirements.
            </Para>

            <SubHeading>Location Information</SubHeading>
            <Para>
              With your permission, we collect approximate (coarse) location
              data from your device. We use this information to recommend nearby
              services, personalize your experience, and improve the accuracy of
              our at-home service bookings. You can disable location access at
              any time through your device settings.
            </Para>

            <SubHeading>Contacts</SubHeading>
            <Para>
              With your permission, we access your device's contacts to help you
              invite friends, share your wardrobe, or connect with our styling
              partners. We do not use your contacts for any purpose other than
              the features you choose to use, and we do not sell or share this
              information with third parties.
            </Para>

            <SubHeading>Photos and Media</SubHeading>
            <Para>
              With your permission, we access your device's photo library so you
              can upload images of your clothing items to build and manage your
              digital wardrobe. These photos are stored securely and are used
              solely to provide the wardrobe organization features of the
              Service.
            </Para>
          </Section>

          {/* ---------- Use of Information ---------- */}
          <Section title="Use of Information">
            <Para>
              We collect, use, and store your personal information to:
            </Para>
            <List
              items={[
                "allow you to access and use our platform & services",
                "communicate with you",
                "maintain your digital wardrobe and offer related services",
                "process requests and orders (including membership registration)",
                "provide technical support",
                "store information about your preferences",
                "recognize you when you return to our Site",
                "Administer and secure the Platform and our services",
                "comply with any procedures, laws, and regulations which apply to us – this may include where we reasonably consider it is in our legitimate interests or the legitimate interests of others to comply, as well as where we are legally required to do so (subject to our making all reasonable efforts, consistent with applicable laws and regulations, to protect our Users' identities and personal information from disclosure to third parties); and",
                "establish, exercise, or defend our legal rights – this may include where we reasonably consider it is in our legitimate interests or the legitimate interests of others, as well as where we are legally required to do so.",
              ]}
            />
            <Para>
              If you have connected an email account, we use that data solely to
              create / access your account at our platform. We do not use your
              data for any tracking purposes including Physical Address, Email
              Address, Coarse Location, and Name. We do not use your email data
              for advertising, to build profiles beyond our platform, or for any
              unrelated purpose. We do not use data from Google APIs to train
              generalized AI or machine learning models.
            </Para>
          </Section>

          {/* ---------- Sharing of Information ---------- */}
          <Section title="Sharing of Information">
            <Para>
              We do not share your Personal Information with third parties
              unless required to comply with applicable laws and regulations, to
              respond to a subpoena, search warrant, or other lawful requests
              for information we receive, or to otherwise protect our rights.
            </Para>
            <Para>
              We keep your personal information for no longer than necessary for
              the purposes for which the personal information is processed. The
              length of time we retain personal information depends on the
              purposes for which we collect and use it and/or as required to
              comply with applicable laws and to establish, exercise, or defend
              our legal rights.
            </Para>
            <Para>
              To enforce the terms of this Privacy Policy, resolve disputes, or
              provide information to satisfy applicable governmental laws,
              regulations, investigations, or proceedings, we may need to retain
              your personal information in our files. We will do so in
              compliance with applicable law.
            </Para>
          </Section>

          {/* ---------- Security ---------- */}
          <Section title="Security">
            <Para>
              We maintain administrative, technical, and physical safeguards
              designed to protect your personal information. All data
              transmitted between our service and third-party APIs is protected
              using industry-standard encryption.
            </Para>
          </Section>

          {/* ---------- Your Rights & Data Deletion ---------- */}
          <Section title="Your Rights & Data Deletion">
            <Para>
              You can delete your account directly from within the app. Go to{" "}
              <strong className="font-medium">
                Edit Profile → Delete Account
              </strong>{" "}
              and follow the steps below:
            </Para>
            <ol className="m-0 pl-6 flex flex-col items-start justify-start gap-[10px] list-decimal">
              <li className="leading-[28px] font-light">
                Tap <strong className="font-medium">Delete Account</strong> —
                you'll see a warning that this action is irreversible and all
                your data will be lost.
              </li>
              <li className="leading-[28px] font-light">
                Confirm your request — the app will open your email client with
                a pre-filled deletion request to{" "}
                <a
                  href="mailto:hello@closetrove.com"
                  className="text-darkslategray-100 underline"
                >
                  hello@closetrove.com
                </a>
                . Simply send that email to complete the request.
              </li>
              <li className="leading-[28px] font-light">
                Your local data is cleared immediately — the app ends your
                session, removes your stored profile, clears your chat history
                from the device, and logs you out.
              </li>
            </ol>
            <Para>
              Once we receive your email, we will permanently delete your
              account and all associated data from our servers. This action
              cannot be undone.
            </Para>
          </Section>

          {/* ---------- Updates ---------- */}
          <Section title="Update to this Privacy Policy">
            <Para>
              We reserve the right to update this policy based on changes in our
              business practice or for other operational, legal, or regulatory
              reasons.
            </Para>
          </Section>

          {/* ---------- Contact ---------- */}
          <Section title="Contact Us">
            <Para>
              If you have any questions about this Privacy Policy, or you would
              like to access, correct, or delete your personal information,
              write to us at{" "}
              <a
                href="mailto:hello@closetrove.com"
                className="text-darkslategray-100 underline"
              >
                hello@closetrove.com
              </a>
              .
            </Para>
          </Section>
        </div>
      </section>

      <SectionSecuritysectionTemp />

      {showModal ? (
        <>
          <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50">
            <div className="w-[70%] justify-center items-center flex flex-col overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
              <PopUp showModal={showModal} setShowModal={setShowModal} />
            </div>
          </div>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
        </>
      ) : null}
    </div>
  );
};

export default PrivacyPolicy;
